import { useCallback, useMemo, useState } from "react";
import { ImageFallback } from "./ImageFallback";
import { useChatImageGalleryStore } from "@store/chat-image-gallery";
import { isValidUrl } from "@shared/utils/validators";

export type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  errorMessage?: string;
  maxWidth?: number;
  canZoomIn?: boolean;
  fallback?: React.ReactNode;
};

export const Image = (props: ImageProps) => {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const {
    errorMessage = "Image not found",
    canZoomIn = true,
    fallback,
    ...rest
  } = props;
  const setChatGalleryImage = useChatImageGalleryStore(
    (state) => state.setChatGalleryImage
  );

  const src = props.src;

  const { height, maxWidth } = useMemo(() => {
    const maxWidth = props.maxWidth ?? 480;

    if (!src || !isValidUrl(src)) return { height: 0, maxWidth };

    const url = new URL(src);
    const params = new URLSearchParams(url.search);
    const originalWidth = parseInt(params.get("width") || "0", 10);
    const originalHeight = parseInt(params.get("height") || "0", 10);

    if (originalWidth && originalHeight) {
      const aspectRatio = originalHeight / originalWidth;
      const maxWidth = Math.min(originalWidth, 480);
      return {
        height: Math.round(maxWidth * aspectRatio),
        maxWidth,
      };
    }
    return { height: 0, maxWidth };
  }, [src, props.maxWidth]);

  const handleImageError = () => {
    setError(true);
    setIsLoading(false);
  };

  const title = useMemo(() => {
    const alt = rest.alt ?? "Photo";
    return `${alt.charAt(0).toUpperCase()}${alt.substring(1, alt.length)}`;
  }, [rest]);

  const handleImageClick = useCallback(async () => {
    if (error || !canZoomIn) return;
    setChatGalleryImage(props);
  }, [error, canZoomIn, props, setChatGalleryImage]);

  return (
    <>
      {fallback && isLoading && !error && fallback}
      {!fallback && isLoading && !error && height > 0 && (
        <ImageFallback
          width={`${maxWidth}px`}
          height={height ? `${height}px` : "auto"}
        />
      )}
      {error ? (
        <span>
          <a href={props.src} target="_blank" rel="noreferrer">
            {errorMessage}
          </a>
        </span>
      ) : (
        <img
          {...rest}
          alt={title}
          onError={handleImageError}
          onLoad={() => setIsLoading(false)}
          onClick={handleImageClick}
          style={
            isLoading
              ? { display: "none" }
              : { cursor: canZoomIn ? "zoom-in" : "default", maxWidth }
          }
        />
      )}
    </>
  );
};
